import React, { memo } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Icon,
  Typography as TypographyBase,
  Paper,
  Grid as GridBase,
} from "@mui/material";
import { LaptopOutlined } from "@mui/icons-material";
import ImageRP from "@/components/ImageRP";
import { normalizePhone } from "@/helpers/normalizers";
import useCustomerPortal from "@/helpers/hooks/useCustomerPortal";
import { BRAND_CONST, USER_ROLE } from "@/config/constants";
import RpSvgIcon from "@/components/icons/RpSvgIcon";
import { capitalizeEachWord } from "@/helpers/utils";
import { ROUTES } from "@/helpers/routes/routePaths";
import ProfileActionIcon from "../ProfileActionIcon";

const Grid = GridBase as React.ComponentType<any>;
const Typography = TypographyBase as React.ComponentType<any>;

type BrandProfileUser = {
  email?: string;
  first_name?: string;
  last_name?: string;
  mobile_no?: string;
  profile_name?: string;
};

type BrandProfileSelectedBrand = {
  brand_name?: string;
  id?: number | string;
};

const BrandProfileCard = ({
  loggedInUserRole,
  user = {},
  setCheckCurrentPass,
  setEdit,
  selectedBrand,
  setShowEditProfileModal = () => {},
}: {
  loggedInUserRole?: string;
  selectedBrand?: BrandProfileSelectedBrand;
  setCheckCurrentPass: (_value: boolean) => void;
  setEdit: (_value: boolean) => void;
  setShowEditProfileModal?: (_value: boolean) => void;
  user?: BrandProfileUser;
}) => {
  const { navigateToCustomerPortal } = useCustomerPortal();
  const { first_name, last_name, email, mobile_no, profile_name } = user || {};
  // Optionally, you can use brandProfileDetails for rendering below
  const handleEditPrfile = () => {
    setShowEditProfileModal(true);
    setEdit(true);
  };
  return (
    <Paper
      elevation={2}
      className="card shadow border-0 mb-4 brand-profile-card "
    >
      <Grid container className="w-100 card-container">
        <Grid
          item
          size={{ xl: 4, md: 3, xs: 12 }}
          className="d-flex flex-column justify-content-center align-items-center avatar-section"
        >
          <div className="avatar-container">
            <ImageRP
              className="profile-image"
              src={profile_name}
              type="PROFILE"
            />
          </div>
        </Grid>
        {/* Main content: info and actions */}
        <Grid
          item
          size={{ xl: 4, md: 3, xs: 12 }}
          className="p-1"
          alignContent="center"
          justifyContent="center"
        >
          <Grid item className="mt-1">
            <Typography variant="h6">
              {capitalizeEachWord(first_name)} {capitalizeEachWord(last_name)}
            </Typography>
          </Grid>
          <Grid item className="mt-3">
            <Typography variant="p" color="text.primary">
              {selectedBrand?.brand_name}
            </Typography>
          </Grid>
          <Grid item className="mt-3">
            <Icon
              component={RpSvgIcon}
              name="email"
              color="primary"
              className="icon"
              fontSize="small"
            />
            <span className="text-break info-text ml-2">{email}</span>
          </Grid>

          <Grid item className="mt-3">
            <Icon
              component={RpSvgIcon}
              name="contact_icon"
              color="primary"
              className="icon"
              fontSize="small"
            />
            <span className="text-break info-text ml-2">
              {mobile_no ? normalizePhone(mobile_no) : "N/A"}
            </span>
          </Grid>
        </Grid>

        <Grid item size={{ xl: 4, md: 6, xs: 12 }} spacing={2} className="p-3">
          <Grid container spacing={3} className="mt-4">
            {(loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
              loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
              loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME) && (
              <Grid item size={{ lg: 6, md: 12, xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  component={Link}
                  to={ROUTES.OVERVIEW_DASHBOARD}
                  fullWidth
                  startIcon={<LaptopOutlined fontSize="small" />}
                >
                  Overview Dashboard
                </Button>
              </Grid>
            )}
            <Grid item size={{ lg: 6, md: 12, xs: 12 }}>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to={ROUTES.DASHBOARD}
                fullWidth
                startIcon={<LaptopOutlined fontSize="small" />}
              >
                {loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
                loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
                loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME
                  ? "Brand Dashboard"
                  : "Dashboard"}
              </Button>
            </Grid>
            <Grid item size={{ lg: 6, md: 12, xs: 12 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ProfileActionIcon src="/img/icons/lock.svg" />}
                onClick={() => setCheckCurrentPass(true)}
                fullWidth
              >
                Change Password
              </Button>
            </Grid>

            <Grid item size={{ lg: 6, md: 12, xs: 12 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={
                  <ProfileActionIcon src="/img/icons/profile_edit.svg" />
                }
                onClick={() => handleEditPrfile()}
                fullWidth
              >
                Edit Profile
              </Button>
            </Grid>
            {selectedBrand?.id !== BRAND_CONST.MS_BRAND_ID &&
              selectedBrand?.id !== BRAND_CONST.KUMON_BRAND_ID &&
              loggedInUserRole === USER_ROLE.PARTICIPANT.NAME && (
                <Grid item size={{ lg: 6, md: 12, xs: 12 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={navigateToCustomerPortal}
                    fullWidth
                    startIcon={
                      <ProfileActionIcon src="/img/icons/manage_payment.svg" />
                    }
                  >
                    Manage Payments
                  </Button>
                </Grid>
              )}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default memo(BrandProfileCard);
